import { GATHER_BASE_PER_HOUR, GATHER_GROWTH, MARCH_MAX_MINUTES, MARCH_TILES_PER_MIN, NODE_DEPOSITS, PVP, type StoredResource } from "@zend/content";

export function tileDistance(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay);
}

/** March duration in ms for a distance in tiles at 10 tiles/min plus speed bonuses (R42). */
export function marchMs(distance: number, speedPct = 0): number {
  const tilesPerMin = MARCH_TILES_PER_MIN * (1 + Math.max(-50, speedPct) / 100);
  return Math.max(1000, Math.round((distance / tilesPerMin) * 60_000));
}

export function withinRange(distance: number, speedPct = 0): boolean {
  return marchMs(distance, speedPct) <= MARCH_MAX_MINUTES * 60_000;
}

export function nodeDeposit(level: number): number {
  return NODE_DEPOSITS[Math.max(1, Math.min(NODE_DEPOSITS.length, level)) - 1]!;
}

/** Gather rate per hour at a node level with gathering bonuses (research, protocol, VIP, Logistics Depot). */
export function gatherRatePerHour(level: number, bonusPct = 0): number {
  return Math.round(GATHER_BASE_PER_HOUR * Math.pow(GATHER_GROWTH, Math.max(1, level) - 1) * (1 + bonusPct / 100));
}

/** How much a squad takes and how long it stays: limited by the remaining deposit and the squad cargo. */
export function gatherPlan(ratePerHour: number, remaining: number, cargo: number): { amount: number; ms: number } {
  const amount = Math.max(0, Math.floor(Math.min(remaining, cargo)));
  const ms = ratePerHour > 0 ? Math.ceil((amount / ratePerHour) * 3_600_000) : 0;
  return { amount, ms };
}

export interface LootInput {
  balances: Record<StoredResource, number>;
  capacity: number;
  protectedPct: number;
  cargo: number;
  hqAttacker: number;
  hqDefender: number;
}

/** PvP loot (R43): 20 % of the unprotected resources, scaled to the cargo, nothing against far weaker targets. */
export function pvpLoot(input: LootInput): { loot: Record<StoredResource, number>; total: number; blocked: "hq_diff" | null } {
  const empty: Record<StoredResource, number> = { RAT: 0, POL: 0, ALY: 0, ENC: 0 };
  if (input.hqAttacker - input.hqDefender > PVP.hqDiffMax) return { loot: empty, total: 0, blocked: "hq_diff" };
  const protectedAmount = input.capacity * (input.protectedPct / 100);
  const wanted: Record<StoredResource, number> = { ...empty };
  let total = 0;
  for (const k of Object.keys(empty) as StoredResource[]) {
    const unprotected = Math.max(0, input.balances[k] - protectedAmount);
    wanted[k] = Math.floor(unprotected * (PVP.lootPct / 100));
    total += wanted[k];
  }
  if (total <= input.cargo || total === 0) return { loot: wanted, total, blocked: null };
  const scale = input.cargo / total;
  const loot: Record<StoredResource, number> = { ...empty };
  let sum = 0;
  for (const k of Object.keys(empty) as StoredResource[]) {
    loot[k] = Math.floor(wanted[k] * scale);
    sum += loot[k];
  }
  return { loot, total: sum, blocked: null };
}

/**
 * Scout detail (R41) from the Sensor Array level against the target's anti-scout level:
 * 0 blocked, 1 HQ and power, 2 + resources and squad size, 3 + full garrison and structures.
 */
export function scoutDetail(sensorLevel: number, antiScout: number): 0 | 1 | 2 | 3 {
  const diff = sensorLevel - antiScout;
  if (diff <= -5) return 0;
  if (diff < 0) return 1;
  if (diff < 5) return 2;
  return 3;
}
